// Vendored from ra-data-graphql@5.15.0 (MIT, Copyright (c) 2016-present
// Francois Zaninotto, Marmelab; full license in ./LICENSE-ra-data-graphql.md):
// https://github.com/marmelab/react-admin/tree/v5.15.0/packages/ra-data-graphql
// Upstream is pinned to @apollo/client v3 (marmelab/react-admin#10971), so we
// own it here. Trimmed to the options this app uses: a caller-provided client,
// introspection, and buildQuery.
import {
  type ApolloClient,
  type DocumentNode,
  type OperationVariables,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { Kind, OperationTypeNode } from 'graphql';
import {
  CREATE,
  type CreateResult,
  type DataProvider,
  DELETE,
  DELETE_MANY,
  type DeleteManyResult,
  type DeleteResult,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  type GetListResult,
  type GetManyReferenceResult,
  type GetManyResult,
  type GetOneResult,
  HttpError,
  type RaRecord,
  UPDATE,
  UPDATE_MANY,
  type UpdateManyResult,
  type UpdateResult,
} from 'react-admin';

import {
  type IntrospectionOptions,
  type IntrospectionResult,
  introspectSchema,
} from './introspection';

type BuildQueryResult = {
  query: DocumentNode;
  variables?: OperationVariables;
  parseResponse: (
    response:
      | ApolloClient.QueryResult<unknown>
      | ApolloClient.MutateResult<unknown>,
  ) => unknown;
};

type BuildQuery = (
  raFetchMethod: string,
  resource: string,
  params: object,
) => BuildQueryResult;

type Options = {
  client: ApolloClient;
  introspection: IntrospectionOptions;
  buildQuery: (introspectionResults: IntrospectionResult) => BuildQuery;
};

const getQueryOperation = (query: DocumentNode): OperationTypeNode => {
  const [definition] = query.definitions;
  if (definition?.kind === Kind.OPERATION_DEFINITION) {
    return definition.operation;
  }

  throw new Error('Unable to determine the query operation');
};

const getSignal = (params: object): AbortSignal | undefined =>
  'signal' in params && params.signal instanceof AbortSignal
    ? params.signal
    : undefined;

// Upstream mapped ApolloError.networkError to its HTTP status, and anything
// else (GraphQL errors) to 200 with the error as the body. Apollo 4 throws the
// error classes directly instead of wrapping them in ApolloError.
const handleError = (error: unknown): never => {
  if (ServerError.is(error) || ServerParseError.is(error)) {
    throw new HttpError(error.message, error.statusCode);
  }

  const message = error instanceof Error ? error.message : String(error);
  // Nest the error rather than using it as the body: Apollo 4's
  // CombinedGraphQLErrors has an `errors` array, and react-admin reads
  // `body.errors` as per-field validation errors. That hid the real message
  // behind "The form is not valid" on pessimistic create/edit forms.
  throw new HttpError(message, 200, { error });
};

const buildGraphQLProvider = ({
  client,
  introspection,
  buildQuery: buildQueryFactory,
}: Options): DataProvider => {
  let introspectionResultsPromise: Promise<IntrospectionResult> | undefined;

  const callApollo = async <TResult>(
    raFetchMethod: string,
    resource: string,
    params: object,
  ): Promise<TResult> => {
    introspectionResultsPromise ??= introspectSchema(client, introspection);
    const introspectionResults = await introspectionResultsPromise;

    const buildQuery = buildQueryFactory(introspectionResults);
    const { parseResponse, query, variables } = buildQuery(
      raFetchMethod,
      resource,
      params,
    );

    if (getQueryOperation(query) === OperationTypeNode.QUERY) {
      return client
        .query({
          query,
          variables,
          fetchPolicy: 'network-only',
          context: { fetchOptions: { signal: getSignal(params) } },
        })
        .then((response) => parseResponse(response) as TResult)
        .catch(handleError);
    }

    return client
      .mutate({ mutation: query, variables })
      .then((response) => parseResponse(response) as TResult)
      .catch(handleError);
  };

  return {
    create: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<CreateResult<RecordType>>(CREATE, resource, params),
    delete: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<DeleteResult<RecordType>>(DELETE, resource, params),
    deleteMany: <RecordType extends RaRecord>(
      resource: string,
      params: object,
    ) =>
      callApollo<DeleteManyResult<RecordType>>(DELETE_MANY, resource, params),
    getList: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<GetListResult<RecordType>>(GET_LIST, resource, params),
    getMany: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<GetManyResult<RecordType>>(GET_MANY, resource, params),
    getManyReference: <RecordType extends RaRecord>(
      resource: string,
      params: object,
    ) =>
      callApollo<GetManyReferenceResult<RecordType>>(
        GET_MANY_REFERENCE,
        resource,
        params,
      ),
    getOne: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<GetOneResult<RecordType>>(GET_ONE, resource, params),
    update: <RecordType extends RaRecord>(resource: string, params: object) =>
      callApollo<UpdateResult<RecordType>>(UPDATE, resource, params),
    updateMany: <RecordType extends RaRecord>(
      resource: string,
      params: object,
    ) =>
      callApollo<UpdateManyResult<RecordType>>(UPDATE_MANY, resource, params),
  };
};

export default buildGraphQLProvider;
